import type { PotentielData } from "@/types"

export interface MetricRow {
  label: string
  unit: string
  current: string
  plu: string
  diff: string
  progress: number | null
  isPositive: boolean
  isNegative: boolean
  confidence?: string | null
  sources?: { page: number; extrait: string; pdfUrl?: string }[]
  isParentValue?: boolean
  parentLibelle?: string | null
}

export interface ComputedPotentiel {
  rows: MetricRow[]
  sdpCurrent: number | null
  sdpPlu: number | null
  sdpIncrease: number | null
  sdpIncreasePct: number | null
  shabCurrent: number | null
  shabPlu: number | null
  shabIncrease: number | null
  shabIncreasePct: number | null
  empriseCurrentPct: number | null
  emprisePluPct: number | null
  floorsCurrent: number | null
  floorsPlu: number | null
  floorsCurrentEstimated: boolean
  floorsPluEstimated: boolean
  anneeConstruction: string | null
}

const fmt = (v: number, unit: string) => `${v.toLocaleString("fr-FR")} ${unit}`
const NA = "—"

export function computePotentiel(data: PotentielData): ComputedPotentiel {
  const { parcel, buildings, plu_zone } = data
  const parcelSurface = parcel?.contenance ?? 0

  const pEmprise = plu_zone?.empriseAuSol
  const pHauteur = plu_zone?.hauteurMax
  const pEtage = plu_zone?.etageMax

  const emprisePercent = pEmprise?.value ?? null
  const maxHeight = pHauteur?.value ?? null
  // PLU values
  const empriseRatio = emprisePercent != null ? emprisePercent / 100 : null
  const potentialEmprise = empriseRatio != null ? parcelSurface * empriseRatio : null
  const pEtageVal = pEtage?.value ?? null
  const pluFloors = maxHeight != null ? Math.floor(maxHeight / 3) : null
  const effectiveFloors = pEtageVal != null ? pEtageVal : pluFloors
  const potentialSDP = potentialEmprise != null && effectiveFloors != null ? potentialEmprise * effectiveFloors : null
  const potentialSHAB = potentialSDP != null ? potentialSDP * 0.8 : null

  // PLU floors estimation flag
  const floorsPluEstimated = pEtageVal == null && pluFloors != null

  // Aggregate current building values across all buildings
  const validBuildings = buildings.filter((b) => b.surface_emprise_sol && b.surface_emprise_sol > 0)
  const curEmprise = validBuildings.length > 0
    ? Math.round(validBuildings.reduce((sum, b) => sum + (b.surface_emprise_sol ?? 0), 0))
    : null
  const curHeight = validBuildings.length > 0
    ? Math.round(Math.max(...validBuildings.map((b) => b.hauteur_mean ?? 0)))
    : null
  const curEmprisePercent = curEmprise && parcelSurface ? Math.round((curEmprise / parcelSurface) * 100) : null

  // Compute SDP per building then sum: each building's emprise × its own floors
  let curSDP: number | null = null
  let floorsCurrentEstimated = false
  if (validBuildings.length > 0) {
    let sdpTotal = 0
    let hasAny = false
    for (const b of validBuildings) {
      const emprise = b.surface_emprise_sol ?? 0
      const floorsRaw = b.nb_niveau ?? null
      const h = b.hauteur_mean ? Math.round(b.hauteur_mean) : null
      const floorsFromHeight = h != null ? Math.floor(h / 3) : null
      const floors = floorsRaw ?? floorsFromHeight
      if (floorsRaw == null && floorsFromHeight != null) floorsCurrentEstimated = true
      if (emprise > 0 && floors && floors > 0) {
        sdpTotal += emprise * floors
        hasAny = true
      }
    }
    curSDP = hasAny ? Math.round(sdpTotal) : null
  }

  // Weighted average floors for display (total SDP / total emprise)
  const curFloors = curSDP != null && curEmprise && curEmprise > 0
    ? Math.round(curSDP / curEmprise)
    : null
  const curSHAB = curSDP ? Math.round(curSDP * 0.8) : null

  // SDP increase
  const sdpIncrease = curSDP != null && potentialSDP != null ? Math.round(potentialSDP - curSDP) : null
  const sdpIncreasePct = curSDP && potentialSDP ? Math.round(((potentialSDP - curSDP) / curSDP) * 100) : null

  // SHAB increase
  const shabIncrease = curSHAB != null && potentialSHAB != null ? Math.round(potentialSHAB - curSHAB) : null
  const shabIncreasePct = curSHAB && potentialSHAB ? Math.round(((potentialSHAB - curSHAB) / curSHAB) * 100) : null

  const delta = (cur: number | null, pot: number | null, unit: string) => {
    if (cur == null || cur === 0 || pot == null) return NA
    const diff = pot - cur
    const pct = Math.round((diff / cur) * 100)
    const sign = diff >= 0 ? "+" : ""
    return `${sign}${Math.round(diff).toLocaleString("fr-FR")} ${unit} (${sign}${pct}%)`
  }

  const deltaSimple = (cur: number | null, pot: number | null) => {
    if (cur == null || cur === 0 || pot == null) return NA
    const diff = pot - cur
    const sign = diff >= 0 ? "+" : ""
    return `${sign}${diff}`
  }

  const progress = (cur: number | null, max: number | null): number | null => {
    if (cur == null || cur === 0 || max == null || max === 0) return null
    return Math.min(100, Math.round((cur / max) * 100))
  }

  const isPos = (cur: number | null, pot: number | null) => cur != null && cur !== 0 && pot != null && pot > cur

  const isNeg = (cur: number | null, pot: number | null) => cur != null && cur !== 0 && pot != null && pot < cur

  const rows: MetricRow[] = [
    {
      label: "Emprise",
      unit: "%",
      current: curEmprisePercent != null ? `${curEmprisePercent}%` : NA,
      plu: emprisePercent != null ? `${Math.round(emprisePercent)}%` : NA,
      diff:
        curEmprisePercent != null && emprisePercent != null
          ? `${Math.round(emprisePercent) >= curEmprisePercent ? "+" : ""}${Math.round(emprisePercent) - curEmprisePercent} pts`
          : NA,
      progress: progress(curEmprisePercent, emprisePercent != null ? Math.round(emprisePercent) : null),
      isPositive: curEmprisePercent != null && emprisePercent != null && emprisePercent > curEmprisePercent,
      isNegative: curEmprisePercent != null && emprisePercent != null && emprisePercent < curEmprisePercent,
      confidence: pEmprise?.confidence,
      sources: pEmprise?.sources,
      isParentValue: pEmprise?.parent_value,
      parentLibelle: plu_zone?.parentLibelle,
    },
    {
      label: "Hauteur",
      unit: "m",
      current: curHeight ? fmt(curHeight, "m") : NA,
      plu: maxHeight != null ? fmt(maxHeight, "m") : NA,
      diff: delta(curHeight, maxHeight, "m"),
      progress: progress(curHeight, maxHeight),
      isPositive: isPos(curHeight, maxHeight),
      isNegative: isNeg(curHeight, maxHeight),
      confidence: pHauteur?.confidence,
      sources: pHauteur?.sources,
      isParentValue: pHauteur?.parent_value,
      parentLibelle: plu_zone?.parentLibelle,
    },
    {
      label: "Etages",
      unit: "",
      current: curFloors ? `${curFloors}` : NA,
      plu: effectiveFloors != null ? `${effectiveFloors}` : NA,
      diff: deltaSimple(curFloors, effectiveFloors),
      progress: progress(curFloors, effectiveFloors),
      isPositive: isPos(curFloors, effectiveFloors),
      isNegative: isNeg(curFloors, effectiveFloors),
      confidence: pEtage?.confidence,
      sources: pEtage?.sources,
      isParentValue: pEtage?.parent_value,
      parentLibelle: plu_zone?.parentLibelle,
    },
    {
      label: "SDP",
      unit: "m²",
      current: curSDP ? fmt(curSDP, "m²") : NA,
      plu: potentialSDP != null ? fmt(Math.round(potentialSDP), "m²") : NA,
      diff: delta(curSDP, potentialSDP != null ? Math.round(potentialSDP) : null, "m²"),
      progress: progress(curSDP, potentialSDP != null ? Math.round(potentialSDP) : null),
      isPositive: isPos(curSDP, potentialSDP != null ? Math.round(potentialSDP) : null),
      isNegative: isNeg(curSDP, potentialSDP != null ? Math.round(potentialSDP) : null),
    },
    {
      label: "SHAB",
      unit: "m²",
      current: curSHAB ? fmt(curSHAB, "m²") : NA,
      plu: potentialSHAB != null ? fmt(Math.round(potentialSHAB), "m²") : NA,
      diff: delta(curSHAB, potentialSHAB != null ? Math.round(potentialSHAB) : null, "m²"),
      progress: progress(curSHAB, potentialSHAB != null ? Math.round(potentialSHAB) : null),
      isPositive: isPos(curSHAB, potentialSHAB != null ? Math.round(potentialSHAB) : null),
      isNegative: isNeg(curSHAB, potentialSHAB != null ? Math.round(potentialSHAB) : null),
    },
  ]

  return {
    rows,
    sdpCurrent: curSDP,
    sdpPlu: potentialSDP != null ? Math.round(potentialSDP) : null,
    sdpIncrease,
    sdpIncreasePct,
    shabCurrent: curSHAB,
    shabPlu: potentialSHAB != null ? Math.round(potentialSHAB) : null,
    shabIncrease,
    shabIncreasePct,
    empriseCurrentPct: curEmprisePercent,
    emprisePluPct: emprisePercent != null ? Math.round(emprisePercent) : null,
    floorsCurrent: curFloors,
    floorsPlu: effectiveFloors,
    floorsCurrentEstimated,
    floorsPluEstimated,
    anneeConstruction: buildings.length > 0
      ? buildings.map((b) => b.annee_construction).filter(Boolean).sort()[0] ?? null
      : null,
  }
}
